import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  VENDOR_CHANNEL_BROADCAST,
  type TypingActor,
  type VendorBroadcastEvent,
} from '@leadsync/shared-types';
import { getSocket } from './socket';
import { trpc } from './trpc';
import {
  THREAD_PAGE_LIMIT,
  TIP_FETCH_OPTS,
  createCoalescer,
  mergeNewestPage,
} from './thread-cache';

/**
 * Keeps one open thread in sync with realtime broadcasts, push-first.
 *
 * Every channel used to answer each broadcast with a full page refetch, which
 * costs one HTTP round-trip per connected agent per message and does not scale
 * on a busy inbox. Most events already carry the complete message, so:
 *
 *   • event has a message  -> apply it straight to the cache. No network.
 *   • event has none       -> schedule a reconciliation tip refetch, coalesced
 *                             so a burst of events costs one request.
 *
 * `threadKey` scopes the coalescer: switching conversation starts a fresh one
 * so a refetch queued for the previous thread can't land on the new one.
 */
function useThreadSync<M extends { id: number }>(
  threadKey: string | number | null,
  handlers: { applyMessage: (message: M) => void; fetchTip: () => Promise<unknown> },
): (message: M | null | undefined) => void {
  // Handlers are rebuilt every render; a ref keeps the coalescer stable
  // without stale closures.
  const handlersRef = useRef(handlers);
  handlersRef.current = handlers;

  const coalescer = useMemo(
    () => createCoalescer(() => handlersRef.current.fetchTip()),
    // eslint-disable-next-line react-hooks/exhaustive-deps -- reset per thread
    [threadKey],
  );

  return useCallback(
    (message: M | null | undefined) => {
      if (message) handlersRef.current.applyMessage(message);
      else coalescer.schedule();
    },
    [coalescer],
  );
}

/**
 * Wires Socket.IO `VendorChannelBroadcast` events into TanStack Query
 * cache invalidations so the UI stays in sync without manual reducers.
 *
 * Event names mirror SignalR / .NET `VendorBroadcastService`:
 *   NewMessage / StatusUpdate / MessageDeleted / MessageEdited /
 *   ReactionUpdate / UnreadCountUpdate / ContactUpdated /
 *   AssignmentChanged / LabelsChanged
 */
export function useChatRealtime() {
  const utils = trpc.useUtils();

  useEffect(() => {
    const socket = getSocket();
    function onBroadcast(evt: VendorBroadcastEvent) {
      switch (evt.eventType) {
        case 'NewMessage':
        case 'MessageEdited':
        case 'MessageDeleted':
        case 'ReactionUpdate':
          // Sidebar last-message + unread. The open thread itself is updated by
          // useChatThreadRealtime (cache-merge, not invalidation — see there).
          void utils.chat.contacts.invalidate();
          if (evt.contactId !== undefined) {
            void utils.chat.openingBundle.invalidate({ contactId: evt.contactId });
          }
          break;
        // Messenger + Instagram channels feed the unified chat sidebar (their
        // conversations link to Contact rows). Refresh the sidebar + unread
        // badge, and — when the event carries the linked contactId — also
        // invalidate the OPEN thread so a new FB/IG message appears live in the
        // unified pane without reopening it (matching the WhatsApp branch).
        // The dedicated /vendor/{facebook,instagram}/inbox routes keep their
        // own conversationId-keyed listeners (useMessengerRealtime / …).
        case 'FacebookNewMessage':
        case 'FacebookStatusUpdate':
        case 'InstagramNewMessage':
        case 'InstagramReaction':
        case 'InstagramStatusUpdate':
          void utils.chat.contacts.invalidate();
          void utils.chat.totalUnread.invalidate();
          if (evt.contactId !== undefined) {
            void utils.chat.openingBundle.invalidate({ contactId: evt.contactId });
          }
          break;
        // StatusUpdate (sent → delivered → read) only affects the open thread's
        // bubbles, which useChatThreadRealtime refreshes via cache-merge.
        case 'UnreadCountUpdate':
          void utils.chat.totalUnread.invalidate();
          void utils.chat.contacts.invalidate();
          break;
        case 'ContactUpdated':
        case 'AssignmentChanged':
        case 'LabelsChanged':
          if (evt.contactId !== undefined) {
            void utils.chat.openingBundle.invalidate({ contactId: evt.contactId });
          }
          void utils.chat.contacts.invalidate();
          break;
        case 'FormResponseReceived':
          // A contact submitted a WhatsApp Flow — refresh the Responses tab
          // and the conversation (autofill/label may have changed the CRM panel).
          void utils.forms.responses.invalidate();
          if (evt.contactId) {
            void utils.chat.openingBundle.invalidate({ contactId: evt.contactId });
          }
          break;
      }
    }
    socket.on(VENDOR_CHANNEL_BROADCAST, onBroadcast);
    return () => {
      socket.off(VENDOR_CHANNEL_BROADCAST, onBroadcast);
    };
  }, [utils]);
}

/**
 * Per-thread realtime for the OPEN conversation.
 *
 * The message timeline is a keyset `useInfiniteQuery`, so it must NOT be
 * invalidated (that refetches every loaded page with its stored cursor, and a
 * fresh tip message shifts the page boundary, dropping a row). Instead the
 * owning component applies a single pushed message (`applyMessage`), refetches
 * just the newest page (`fetchTip`), or removes a row (`onDelete`).
 *
 * WhatsApp events carry the full `ChatMessageDto` on `evt.log` whenever the
 * producer had one, so the common case costs no network at all. Events without
 * a payload — an echo of a send made from another BSP client, a campaign batch
 * — fall back to a coalesced tip refetch. Scoped to `contactId` so background
 * threads are ignored.
 */
export function useChatThreadRealtime<M extends { id: number }>(
  contactId: number | null,
  handlers: {
    applyMessage: (message: M) => void;
    fetchTip: () => Promise<unknown>;
    onDelete: (messageLogId: number) => void;
  },
) {
  const handlersRef = useRef(handlers);
  handlersRef.current = handlers;
  const sync = useThreadSync<M>(contactId, handlers);

  useEffect(() => {
    if (contactId === null) return;
    const socket = getSocket();
    function onBroadcast(evt: VendorBroadcastEvent) {
      if (!('contactId' in evt) || evt.contactId !== contactId) return;
      switch (evt.eventType) {
        case 'NewMessage':
        case 'MessageEdited':
        case 'ReactionUpdate':
          // `log` is a complete DTO when present (see the contract in
          // shared-types/realtime.ts) — apply it and skip the round-trip.
          //
          // The one cast in this path, at the deserialization boundary where it
          // belongs: `ChatMessageDto` types date fields `string | Date` because
          // it is shared with the publisher, while the thread cache holds the
          // tRPC row type where they are `string`. Both are ISO strings on the
          // wire — Socket.IO JSON-encodes, and tRPC runs no date transformer —
          // and every consumer normalises with `new Date(...)`.
          sync(evt.log as M | undefined);
          break;
        case 'StatusUpdate':
        case 'FacebookNewMessage':
        case 'FacebookStatusUpdate':
        case 'InstagramNewMessage':
        case 'InstagramReaction':
        case 'InstagramStatusUpdate':
          // These carry no message payload; reconcile.
          sync(null);
          break;
        case 'MessageDeleted':
          handlersRef.current.onDelete(evt.messageLogId);
          break;
      }
    }
    socket.on(VENDOR_CHANNEL_BROADCAST, onBroadcast);
    return () => {
      socket.off(VENDOR_CHANNEL_BROADCAST, onBroadcast);
    };
  }, [contactId, sync]);
}

/**
 * Keeps the three inbox unread totals fresh from **any** page — these back the
 * nav "Inboxes" badges, which live in the vendor shell (always mounted), not on
 * the inbox routes. Without a global listener a new message or a read on
 * another page would leave the nav count stale until navigation.
 *
 * Per channel: WhatsApp `chat.totalUnread` (NewMessage / UnreadCountUpdate),
 * Messenger `facebook.inbox.totalUnread` (FacebookNewMessage / FacebookUnreadUpdate),
 * Instagram `instagram.inbox.totalUnread` (InstagramNewMessage / InstagramUnreadUpdate).
 */
export function useInboxNavRealtime() {
  const utils = trpc.useUtils();

  useEffect(() => {
    const socket = getSocket();
    function onBroadcast(evt: VendorBroadcastEvent) {
      switch (evt.eventType) {
        case 'NewMessage':
        case 'UnreadCountUpdate':
          void utils.chat.totalUnread.invalidate();
          break;
        case 'FacebookNewMessage':
        case 'FacebookUnreadUpdate':
          void utils.facebook.inbox.totalUnread.invalidate();
          break;
        case 'InstagramNewMessage':
        case 'InstagramUnreadUpdate':
          void utils.instagram.inbox.totalUnread.invalidate();
          break;
      }
    }
    socket.on(VENDOR_CHANNEL_BROADCAST, onBroadcast);
    return () => {
      socket.off(VENDOR_CHANNEL_BROADCAST, onBroadcast);
    };
  }, [utils]);
}

/**
 * Wires Messenger socket events into TanStack Query invalidations for the
 * standalone `/vendor/facebook/inbox` route. (`useChatRealtime` only freshens
 * the unified chat sidebar on FB events — it never touches the Messenger
 * inbox's own `facebook.inbox.*` query keys, so without this hook that inbox
 * stays frozen until a manual reload.)
 *
 * Pass the open `conversationId` so the active thread refreshes precisely;
 * pass `null` when none is open.
 */
export function useMessengerRealtime(activeConversationId: number | null) {
  const utils = trpc.useUtils();

  // Merge the newest page into the open thread's infinite cache (never
  // invalidate — that refetches every loaded page and a fresh tip message
  // shifts the keyset boundary, dropping a row). Messenger broadcasts carry no
  // message payload, so every event reconciles — but through one coalesced
  // request rather than one per event.
  const fetchTip = useCallback(() => {
    const conversationId = activeConversationId;
    if (conversationId === null) return Promise.resolve();
    return utils.facebook.inbox.messages
      .fetch({ conversationId, limit: THREAD_PAGE_LIMIT }, TIP_FETCH_OPTS)
      .then((tip) =>
        utils.facebook.inbox.messages.setInfiniteData(
          { conversationId, limit: THREAD_PAGE_LIMIT },
          (old) => mergeNewestPage(old, tip),
        ),
      );
  }, [utils, activeConversationId]);
  const sync = useThreadSync<{ id: number }>(activeConversationId, {
    applyMessage: () => {},
    fetchTip,
  });

  useEffect(() => {
    const socket = getSocket();
    const isActive = (convId: number | undefined) =>
      activeConversationId !== null && (convId == null || convId === activeConversationId);
    function onBroadcast(evt: VendorBroadcastEvent) {
      switch (evt.eventType) {
        case 'FacebookNewMessage':
          void utils.facebook.inbox.conversations.invalidate();
          if (isActive(evt.conversationId)) sync(null);
          break;
        case 'FacebookStatusUpdate':
          if (isActive(evt.conversationId)) sync(null);
          break;
      }
    }
    socket.on(VENDOR_CHANNEL_BROADCAST, onBroadcast);
    return () => {
      socket.off(VENDOR_CHANNEL_BROADCAST, onBroadcast);
    };
  }, [utils, activeConversationId, sync]);
}

/**
 * Wires Instagram DM socket events into TanStack Query invalidations for the
 * standalone `/vendor/instagram/inbox` route. Mirror of {@link useMessengerRealtime}.
 */
export function useInstagramRealtime(activeConversationId: number | null) {
  const utils = trpc.useUtils();

  // See the Messenger hook — same push-less reconciliation, coalesced.
  const fetchTip = useCallback(() => {
    const conversationId = activeConversationId;
    if (conversationId === null) return Promise.resolve();
    return utils.instagram.inbox.messages
      .fetch({ conversationId, limit: THREAD_PAGE_LIMIT }, TIP_FETCH_OPTS)
      .then((tip) =>
        utils.instagram.inbox.messages.setInfiniteData(
          { conversationId, limit: THREAD_PAGE_LIMIT },
          (old) => mergeNewestPage(old, tip),
        ),
      );
  }, [utils, activeConversationId]);
  const sync = useThreadSync<{ id: number }>(activeConversationId, {
    applyMessage: () => {},
    fetchTip,
  });

  useEffect(() => {
    const socket = getSocket();
    const isActive = (convId: number | undefined) =>
      activeConversationId !== null && (convId == null || convId === activeConversationId);
    function onBroadcast(evt: VendorBroadcastEvent) {
      switch (evt.eventType) {
        case 'InstagramNewMessage':
          void utils.instagram.inbox.conversations.invalidate();
          if (isActive(evt.conversationId)) sync(null);
          break;
        case 'InstagramReaction':
        case 'InstagramStatusUpdate':
          if (isActive(evt.conversationId)) sync(null);
          break;
      }
    }
    socket.on(VENDOR_CHANNEL_BROADCAST, onBroadcast);
    return () => {
      socket.off(VENDOR_CHANNEL_BROADCAST, onBroadcast);
    };
  }, [utils, activeConversationId, sync]);
}

/**
 * Subscribes to `TypingIndicator` broadcasts for a specific contact and
 * returns the most recent actor (user id or 'bot') that fired one in the
 * last 5 seconds. Lets the conversation header surface "Bob is typing…"
 * or "Bot is typing…" so two team members don't double-reply.
 *
 * Pass `null` to disable the subscription (e.g. when no contact is open).
 */
export function useTypingIndicator(contactId: number | null): {
  actor: TypingActor | null;
} {
  const [actor, setActor] = useState<TypingActor | null>(null);
  const clearTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (contactId === null) {
      setActor(null);
      return;
    }
    const socket = getSocket();
    function onBroadcast(evt: VendorBroadcastEvent) {
      if (evt.eventType !== 'TypingIndicator' || evt.contactId !== contactId) return;
      setActor(evt.actor);
      if (clearTimerRef.current) clearTimeout(clearTimerRef.current);
      clearTimerRef.current = setTimeout(() => setActor(null), 5_000);
    }
    socket.on(VENDOR_CHANNEL_BROADCAST, onBroadcast);
    return () => {
      socket.off(VENDOR_CHANNEL_BROADCAST, onBroadcast);
      if (clearTimerRef.current) clearTimeout(clearTimerRef.current);
    };
  }, [contactId]);

  return { actor };
}

/**
 * Realtime cache sync for the vendor dashboard's live message stats.
 *
 * The dashboard's `analytics.dashboard.kpis`, `whatsappTimeseries`,
 * `botsTopFlows`, and `campaignsRecent` tRPC queries all recompute from
 * `WhatsAppMessageLog` / `VendorFlowEvent` / `Campaign` on every fetch,
 * but they fetch only on mount / focus by default — so a message
 * arriving mid-session leaves the KPI tiles stale.
 *
 * This hook listens to the same `VendorChannelBroadcast` events
 * `useChatRealtime` consumes (NewMessage / StatusUpdate / MessageEdited
 * / MessageDeleted / ReactionUpdate) and the FB/IG counterparts, and
 * invalidates the four dashboard query keys so the tiles update within
 * a request round-trip of the inbound/outbound landing.
 *
 * Note on the rollup-backed `msg7d`/`msg30d` numbers: those come from
 * the `VendorMetricSnapshot` table which the `dataSyncQueue` BullMQ job
 * refreshes every 30 minutes. The KPI invalidation here also re-runs
 * the snapshot-fallback path so 24h tiles update live; the 7d/30d
 * tiles inherit the rollup's natural ≤30-min lag, which is acceptable
 * for a 7-day metric.
 */
export function useDashboardRealtime() {
  const utils = trpc.useUtils();

  useEffect(() => {
    const socket = getSocket();
    function onBroadcast(evt: VendorBroadcastEvent) {
      switch (evt.eventType) {
        // Any message movement changes message-volume + delivery-state
        // tiles. Hit all four dashboard queries on a single broadcast.
        case 'NewMessage':
        case 'StatusUpdate':
        case 'MessageEdited':
        case 'MessageDeleted':
        case 'ReactionUpdate':
        case 'FacebookNewMessage':
        case 'FacebookStatusUpdate':
        case 'InstagramNewMessage':
        case 'InstagramStatusUpdate':
        case 'InstagramReaction':
          void utils.analytics.dashboard.kpis.invalidate();
          void utils.analytics.dashboard.whatsappTimeseries.invalidate();
          void utils.analytics.dashboard.botsTopFlows.invalidate();
          void utils.analytics.dashboard.campaignsRecent.invalidate();
          break;
      }
    }
    socket.on(VENDOR_CHANNEL_BROADCAST, onBroadcast);
    return () => {
      socket.off(VENDOR_CHANNEL_BROADCAST, onBroadcast);
    };
  }, [utils]);
}

/**
 * Cache sync for the standalone /vendor/contacts list. Invalidates the list
 * whenever a row's rendered data changes:
 *   • Row shape — AssignmentChanged / ContactUpdated / LabelsChanged (CRM
 *     edits made on the chat page or in another tab).
 *   • Status column (unread badge) + Last-msg cell — an inbound message on
 *     any channel bumps Contact.unreadMessagesCount + messagedAt
 *     (NewMessage / FacebookNewMessage / InstagramNewMessage), and
 *     mark-as-read zeroes the count (UnreadCountUpdate). Without these the
 *     unread badge stayed stale until the 30s staleTime lapsed.
 *
 * StatusUpdate (outbound delivery state) is intentionally NOT handled — it
 * doesn't change any contacts-list column.
 *
 * Inbound traffic can arrive in bursts, and this list query is heavy
 * (group/label/custom-field joins + a count), so invalidations are run on a
 * leading-edge throttle: fire immediately on the first event, then coalesce
 * further events in the cooldown window into a single trailing refetch.
 */
export function useContactsRealtime() {
  const utils = trpc.useUtils();
  const lastRun = useRef(0);
  const trailing = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    const socket = getSocket();
    const COOLDOWN_MS = 750;

    function invalidateList() {
      lastRun.current = Date.now();
      void utils.contacts.contacts.list.invalidate();
    }
    function scheduleInvalidate() {
      const elapsed = Date.now() - lastRun.current;
      if (elapsed >= COOLDOWN_MS) {
        invalidateList();
      } else if (!trailing.current) {
        trailing.current = setTimeout(() => {
          trailing.current = null;
          invalidateList();
        }, COOLDOWN_MS - elapsed);
      }
    }

    function onBroadcast(evt: VendorBroadcastEvent) {
      switch (evt.eventType) {
        case 'ContactUpdated':
        case 'AssignmentChanged':
        case 'LabelsChanged':
        case 'NewMessage':
        case 'UnreadCountUpdate':
        case 'FacebookNewMessage':
        case 'InstagramNewMessage':
          scheduleInvalidate();
          break;
      }
    }
    socket.on(VENDOR_CHANNEL_BROADCAST, onBroadcast);
    return () => {
      socket.off(VENDOR_CHANNEL_BROADCAST, onBroadcast);
      if (trailing.current) {
        clearTimeout(trailing.current);
        trailing.current = null;
      }
    };
  }, [utils]);
}
